import { useState, useEffect } from 'react';
import './Infrastructure.css';

const EDGE_URL = 'https://edge.estream.dev';

// Deployed resource types
interface DeployedResource {
  id: string;
  name: string;
  type: 'vpc' | 'firewall' | 'instance' | 'service_account' | 'static_ip' | 'tunnel';
  status: 'active' | 'pending' | 'error' | 'deleted';
  region?: string;
  zone?: string;
  environment?: string;
  createdAt: string;
  circuitId?: string;
  circuitType?: string;
  externalLink?: string;
}

type ResourceFilter = 'all' | 'vpc' | 'firewall' | 'instance' | 'service_account' | 'static_ip' | 'tunnel';
type ViewMode = 'circuits' | 'resources';

interface InfraCircuit {
  id: string;
  name: string;
  description: string;
  type: string;
  required: boolean;
}

const INFRA_CIRCUITS: InfraCircuit[] = [
  {
    id: 'vpc',
    name: 'Create VPC Network',
    description: 'Create estream-alpha VPC with subnet for edge nodes',
    type: 'estream.ops.create.vpc.v1',
    required: true,
  },
  {
    id: 'firewall-ssh',
    name: 'Firewall: SSH Access',
    description: 'Allow SSH access for administration',
    type: 'estream.ops.firewall.rule.v1',
    required: true,
  },
  {
    id: 'firewall-estream',
    name: 'Firewall: eStream Ports',
    description: 'Allow HTTP/HTTPS for eStream services',
    type: 'estream.ops.firewall.rule.v1',
    required: true,
  },
  {
    id: 'service-account',
    name: 'Service Account',
    description: 'Create ess-scatter service account for node operations',
    type: 'estream.ops.configure.sa.v1',
    required: true,
  },
  {
    id: 'static-ip',
    name: 'Allocate Static IP',
    description: 'Reserve a static IP for the edge node',
    type: 'estream.ops.allocate.ip.v1',
    required: true,
  },
  {
    id: 'edge-node',
    name: 'Deploy Edge Node',
    description: 'Deploy e2-small VM with metrics server',
    type: 'estream.ops.deploy.node.v1',
    required: true,
  },
  {
    id: 'cloudflare-tunnel',
    name: 'Cloudflare Tunnel',
    description: 'Create tunnel to expose edge node via edge.estream.dev',
    type: 'estream.ops.cloudflare.tunnel.v1',
    required: true,
  },
];

type CircuitStatus = 'idle' | 'pending' | 'approved' | 'executing' | 'complete' | 'error';

export default function Infrastructure() {
  const [viewMode, setViewMode] = useState<ViewMode>('resources');
  const [status, setStatus] = useState<Record<string, CircuitStatus>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [resources, setResources] = useState<DeployedResource[]>([]);
  const [resourcesLoading, setResourcesLoading] = useState(true);
  const [resourceFilter, setResourceFilter] = useState<ResourceFilter>('all');
  const [environmentFilter, setEnvironmentFilter] = useState<string>('all');

  // Fetch deployed resources
  useEffect(() => {
    async function fetchResources() {
      try {
        const response = await fetch(`${EDGE_URL}/api/infrastructure`);
        if (response.ok) {
          const data = await response.json();
          setResources(data.resources || []);
        } else {
          // If API doesn't exist yet, show empty state
          setResources([]);
        }
      } catch {
        // Show empty state if API fails
        setResources([]);
      } finally {
        setResourcesLoading(false);
      }
    }
    fetchResources();
    const interval = setInterval(fetchResources, 30000);
    return () => clearInterval(interval);
  }, []);

  // Filter resources
  const filteredResources = resources.filter(r => {
    if (resourceFilter !== 'all' && r.type !== resourceFilter) return false;
    if (environmentFilter !== 'all' && r.environment !== environmentFilter) return false;
    return true;
  });

  // Group resources by type
  const resourcesByType = filteredResources.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {} as Record<string, DeployedResource[]>);

  const submitCircuit = async (circuit: InfraCircuit) => {
    setStatus(prev => ({ ...prev, [circuit.id]: 'pending' }));
    
    try {
      const response = await fetch(`${EDGE_URL}/api/circuits/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: circuit.type,
          environment: 'alpha-devnet',
          description: circuit.description,
          definition: { circuitId: circuit.id },
        }),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to submit: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('[Infrastructure] Submitted:', data);
      setStatus(prev => ({ ...prev, [circuit.id]: 'pending' }));
      return data.circuitId;
    } catch (e) {
      console.error('[Infrastructure] Submit error:', e);
      setStatus(prev => ({ ...prev, [circuit.id]: 'error' }));
      throw e;
    }
  };

  const activateAll = async () => {
    setSubmitting(true);
    setError(null);
    setSuccess(null);
    
    try {
      for (const circuit of INFRA_CIRCUITS) {
        await submitCircuit(circuit);
        // Small delay between submissions
        await new Promise(r => setTimeout(r, 500));
      }
      setSuccess('All circuits submitted! Approve them via Seeker to execute.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to submit circuits');
    } finally {
      setSubmitting(false);
    }
  };

  const allPending = INFRA_CIRCUITS.every(c => status[c.id] === 'pending');
  // const allComplete = INFRA_CIRCUITS.every(c => status[c.id] === 'complete');

  return (
    <div className="infrastructure-page animate-in">
      <div className="page-header">
        <div>
          <h1>Infrastructure</h1>
          <p className="subtitle">Deploy and manage eStream infrastructure</p>
        </div>
        
        {/* View Toggle */}
        <div className="view-toggle">
          <button 
            className={`toggle-btn ${viewMode === 'resources' ? 'active' : ''}`}
            onClick={() => setViewMode('resources')}
          >
            Deployed Resources
          </button>
          <button 
            className={`toggle-btn ${viewMode === 'circuits' ? 'active' : ''}`}
            onClick={() => setViewMode('circuits')}
          >
            Activation Circuits
          </button>
        </div>
      </div>

      {/* Status Banner */}
      {error && (
        <div className="alert alert-error">
          <span>⚠️</span> {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <span>✓</span> {success}
        </div>
      )}

      {/* Deployed Resources View */}
      {viewMode === 'resources' && (
        <>
          {/* Resource Filters */}
          <div className="resource-filters">
            <div className="filter-group">
              <label>Type:</label>
              <select 
                value={resourceFilter} 
                onChange={e => setResourceFilter(e.target.value as ResourceFilter)}
              >
                <option value="all">All Types</option>
                <option value="vpc">VPCs</option>
                <option value="firewall">Firewalls</option>
                <option value="instance">Instances</option>
                <option value="service_account">Service Accounts</option>
                <option value="static_ip">Static IPs</option>
                <option value="tunnel">Tunnels</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Environment:</label>
              <select 
                value={environmentFilter} 
                onChange={e => setEnvironmentFilter(e.target.value)}
              >
                <option value="all">All Environments</option>
                <option value="alpha-devnet">Alpha Devnet</option>
                <option value="testnet">Testnet</option>
                <option value="mainnet">Mainnet</option>
              </select>
            </div>
            <div className="resource-count">
              {filteredResources.length} resource{filteredResources.length !== 1 ? 's' : ''}
            </div>
          </div>

          {/* Resource Cards by Type */}
          {resourcesLoading ? (
            <div className="loading-state">Loading deployed resources...</div>
          ) : filteredResources.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🏗️</span>
              <p>No deployed resources found</p>
              <button 
                className="btn btn-primary"
                onClick={() => setViewMode('circuits')}
              >
                Deploy Infrastructure
              </button>
            </div>
          ) : (
            <div className="resource-grid">
              {Object.entries(resourcesByType).map(([type, items]) => (
                <section key={type} className="resource-section card">
                  <div className="section-header">
                    <h2 className="card-title">
                      <ResourceIcon type={type as DeployedResource['type']} />
                      {getResourceTypeName(type)}
                    </h2>
                    <span className="resource-badge">{items.length}</span>
                  </div>
                  <div className="resource-list">
                    {items.map(resource => (
                      <ResourceCard key={resource.id} resource={resource} />
                    ))}
                  </div>
                </section>
              ))}
            </div>
          )}
        </>
      )}

      {/* Activation Circuits View */}
      {viewMode === 'circuits' && (
        <>
          {/* Quick Actions */}
          <section className="card">
            <h2 className="card-title">Quick Actions</h2>
            <div className="action-buttons">
              <button 
                className="btn btn-primary btn-large"
                onClick={activateAll}
                disabled={submitting || allPending}
              >
                {submitting ? 'Submitting...' : allPending ? 'Awaiting Approval' : '🚀 Activate Alpha-Devnet'}
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => window.open('https://console.cloud.google.com/compute/instances?project=alpha-devnet-484217', '_blank')}
              >
                Open GCP Console
              </button>
            </div>
            <p className="help-text">
              Activation submits governance circuits for VPC, firewall, and edge node creation.
              Each circuit requires approval via Seeker before execution.
            </p>
          </section>

          {/* Circuit List */}
          <section className="card">
            <h2 className="card-title">Infrastructure Circuits</h2>
            <table className="table">
              <thead>
                <tr>
                  <th>Circuit</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {INFRA_CIRCUITS.map(circuit => (
                  <tr key={circuit.id}>
                    <td>
                      <span className="circuit-name">{circuit.name}</span>
                      <code className="circuit-type">{circuit.type}</code>
                    </td>
                    <td>{circuit.description}</td>
                    <td>
                      <StatusBadge status={status[circuit.id] || 'idle'} />
                    </td>
                    <td>
                      <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => submitCircuit(circuit)}
                        disabled={submitting || status[circuit.id] === 'pending'}
                      >
                        Submit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {/* Manual Execution */}
          <section className="card">
            <h2 className="card-title">Manual Execution</h2>
            <p>For immediate infrastructure deployment, run the dogfood script locally:</p>
            <pre className="code-block">
{`# Set environment variables
export ESTREAM_GCP_PROJECT="alpha-devnet-484217"
export ESTREAM_GCP_REGION="us-central1"
export CLOUDFLARE_API_TOKEN="your-token"
export CLOUDFLARE_ACCOUNT_ID="a10ae2809f0aee33d5defcdb5630862c"

# Run dogfood script
cd estream-io/examples/circuits/dogfood
./dogfood.sh`}
            </pre>
            <p className="help-text">
              The dogfood script will create all infrastructure and configure Cloudflare tunnel.
            </p>
          </section>
        </>
      )}
    </div>
  );
}

// Resource Card Component
function ResourceCard({ resource }: { resource: DeployedResource }) {
  const timeAgo = formatTimeAgo(new Date(resource.createdAt).getTime());
  
  return (
    <div className={`resource-card status-${resource.status}`}>
      <div className="resource-header">
        <span className="resource-name">{resource.name}</span>
        <div className="resource-header-right">
          {resource.environment && (
            <span className={`env-badge env-${resource.environment}`}>
              {resource.environment}
            </span>
          )}
          <span className={`status-dot ${resource.status}`} />
        </div>
      </div>
      <div className="resource-details">
        <span className="resource-id">{resource.id}</span>
        {resource.region && <span className="resource-region">{resource.region}</span>}
      </div>
      {resource.circuitId && (
        <div className="resource-circuit">
          <span className="label">Circuit:</span>
          <code>{resource.circuitId.slice(0, 12)}...</code>
        </div>
      )}
      <div className="resource-footer">
        <span className="resource-time">{timeAgo}</span>
        {resource.externalLink && (
          <a 
            href={resource.externalLink} 
            target="_blank" 
            rel="noopener noreferrer"
            className="external-link"
          >
            Open in Console →
          </a>
        )}
      </div>
    </div>
  );
}

// Resource Icon Component
function ResourceIcon({ type }: { type: DeployedResource['type'] }) {
  const icons: Record<DeployedResource['type'], string> = {
    vpc: '🌐',
    firewall: '🛡️',
    instance: '🖥️',
    service_account: '🔑',
    static_ip: '📍',
    tunnel: '🚇',
  };
  return <span className="resource-type-icon">{icons[type] || '📦'}</span>;
}

// Resource type display name
function getResourceTypeName(type: string): string {
  const names: Record<string, string> = {
    vpc: 'VPC Networks',
    firewall: 'Firewall Rules',
    instance: 'Compute Instances',
    service_account: 'Service Accounts',
    static_ip: 'Static IPs',
    tunnel: 'Tunnels',
  };
  return names[type] || type;
}


// Format time ago helper
function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'Just now';
}

function StatusBadge({ status }: { status: CircuitStatus }) {
  const labels: Record<CircuitStatus, { text: string; class: string }> = {
    idle: { text: 'Not Started', class: 'badge-secondary' },
    pending: { text: 'Pending Approval', class: 'badge-warning' },
    approved: { text: 'Approved', class: 'badge-info' },
    executing: { text: 'Executing...', class: 'badge-info' },
    complete: { text: 'Complete', class: 'badge-success' },
    error: { text: 'Error', class: 'badge-error' },
  };
  
  const { text, class: className } = labels[status];
  return <span className={`badge ${className}`}>{text}</span>;
}
